/**
 * Registration Step 2 - Parent 1 Information Form (v2)
 *
 * Migrated to reusable wizard architecture.
 * - Receives data via props (registrationData)
 * - Updates parent via callbacks (onDataChange, onValidationChange)
 * - No context dependency - pure component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FormInput } from '../../FormInput';
import { PhoneNumberInput } from '../../PhoneNumberInput';
import { FormSelect } from '../../FormSelect';
import styles from './Step2ParentForm.module.css';

const Step2ParentForm = ({
  registrationData,
  onDataChange,
  onValidationChange
}) => {
  const data = registrationData.parent1;
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // Validation logic
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required fields
    if (!data.first_name?.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!data.last_name?.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!data.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!data.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(\d{3}\)\s\d{3}-\d{4}$/.test(data.phone)) {
      newErrors.phone = 'Please enter a valid phone number (xxx) xxx-xxxx';
    }
    if (!data.relationship_to_player?.trim()) {
      newErrors.relationship_to_player = 'Relationship to player is required';
    }

    // Address fields
    if (!data.address?.address_line1?.trim()) {
      newErrors['address.address_line1'] = 'Address is required';
    }
    if (!data.address?.city?.trim()) {
      newErrors['address.city'] = 'City is required';
    }
    if (!data.address?.state?.trim()) {
      newErrors['address.state'] = 'State is required';
    }
    if (!data.address?.zip_code?.trim()) {
      newErrors['address.zip_code'] = 'ZIP code is required';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    return isValid;
  }, [data, onValidationChange]);

  // Validate on data change
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setIsDirty(true);

    // Handle nested fields (e.g., "address.address_line1")
    if (name.includes('.')) {
      const [parent, nestedField] = name.split('.');
      onDataChange({
        ...registrationData,
        parent1: {
          ...data,
          [parent]: {
            ...data[parent],
            [nestedField]: fieldValue
          }
        }
      });
    } else {
      onDataChange({
        ...registrationData,
        parent1: {
          ...data,
          [name]: fieldValue
        }
      });
    }
  }, [data, registrationData, onDataChange]);

  const getFieldError = (fieldName) => errors[fieldName] || '';

  return (
    <div>
      <div className={styles.formHeader}>
        <h3 className={styles.formTitle}>
          Primary Parent/Guardian Information
        </h3>
        <p className={styles.formSubtitle}>
          Please provide details for the primary parent or guardian
        </p>
        {isDirty && (
          <div className={`${styles.validationStatus} ${Object.keys(errors).length === 0 ? styles.valid : styles.invalid}`}>
            {Object.keys(errors).length === 0 ? '✓ Form is valid' : 'Please complete all required fields'}
          </div>
        )}
      </div>

      <div className={styles.gridTwoColumn}>
        <FormInput
          label="First Name"
          name="first_name"
          value={data.first_name}
          onChange={handleInputChange}
          error={getFieldError('first_name')}
          required
          placeholder="John"
        />

        <FormInput
          label="Last Name"
          name="last_name"
          value={data.last_name}
          onChange={handleInputChange}
          error={getFieldError('last_name')}
          required
          placeholder="Smith"
        />
      </div>

      <FormInput
        label="Email Address"
        type="email"
        name="email"
        value={data.email}
        onChange={handleInputChange}
        error={getFieldError('email')}
        required
        placeholder="john.smith@email.com"
      />

      <PhoneNumberInput
        label="Phone Number"
        name="phone"
        value={data.phone}
        onChange={handleInputChange}
        error={getFieldError('phone')}
        required
        placeholder="(555) 123-4567"
      />

      <FormInput
        label="Address Line 1"
        name="address.address_line1"
        value={data.address?.address_line1 || ''}
        onChange={handleInputChange}
        error={getFieldError('address.address_line1')}
        required
        placeholder="123 Main Street"
      />

      <FormInput
        label="Address Line 2"
        name="address.address_line2"
        value={data.address?.address_line2 || ''}
        onChange={handleInputChange}
        error={getFieldError('address.address_line2')}
        placeholder="Apt 4B (optional)"
      />

      <div className={styles.gridThreeColumn}>
        <FormInput
          label="City"
          name="address.city"
          value={data.address?.city || ''}
          onChange={handleInputChange}
          error={getFieldError('address.city')}
          required
          placeholder="Los Angeles"
        />

        <FormInput
          label="State"
          name="address.state"
          value={data.address?.state || ''}
          onChange={handleInputChange}
          error={getFieldError('address.state')}
          required
          placeholder="CA"
        />

        <FormInput
          label="ZIP Code"
          name="address.zip_code"
          value={data.address?.zip_code || ''}
          onChange={handleInputChange}
          error={getFieldError('address.zip_code')}
          required
          placeholder="90210"
        />
      </div>

      <FormSelect
        label="Relationship to Player"
        name="relationship_to_player"
        value={data.relationship_to_player}
        onChange={handleInputChange}
        error={getFieldError('relationship_to_player')}
        required
      >
        <option value="">Select...</option>
        <option value="mother">Mother</option>
        <option value="father">Father</option>
        <option value="guardian">Guardian</option>
        <option value="other">Other</option>
      </FormSelect>
    </div>
  );
};

export default Step2ParentForm;
